export const CHANNEL_IN_PERSON = 'in_person';
export const CHANNEL_MARKETPLACE = 'marketplace';
export const CHANNEL_DIGITAL = 'digital';

export const PRODUCT_STOCK = 'physical_stock';
export const PRODUCT_MTO = 'physical_made_to_order';
export const PRODUCT_DIGITAL = 'digital';

export const PRODUCTION_STATUSES = ['not_applicable', 'queued', 'in_progress', 'done'];
export const LOGISTICS_STATUSES = [
    'not_applicable',
    'blocked',
    'awaiting_pack',
    'packing',
    'packed',
    'dispatched',
    'in_transit',
    'delivered',
    'not_delivered',
    'returned',
];
export const PAYOUT_STATUSES = ['not_applicable', 'held', 'released'];

const CHANNEL_LABELS = {
    [CHANNEL_IN_PERSON]: 'Presencial',
    [CHANNEL_MARKETPLACE]: 'Marketplace / loja online',
    [CHANNEL_DIGITAL]: 'Digital',
};

const PRODUCT_LABELS = {
    [PRODUCT_STOCK]: 'Físico em estoque',
    [PRODUCT_MTO]: 'Físico sob encomenda',
    [PRODUCT_DIGITAL]: 'Digital',
};

const PRODUCTION_LABELS = {
    not_applicable: 'Não se aplica',
    queued: 'A produzir',
    in_progress: 'Produzindo',
    done: 'Produzido',
};

export const KANBAN_COLUMNS = [
    { id: 'queued', lane: 'production', group: 'production' },
    { id: 'in_progress', lane: 'production', group: 'production' },
    { id: 'done', lane: 'production', group: 'production' },
    { id: 'awaiting_pack', lane: 'logistics', group: 'logistics' },
    { id: 'packing', lane: 'logistics', group: 'logistics' },
    { id: 'packed', lane: 'logistics', group: 'logistics' },
    { id: 'dispatched', lane: 'logistics', group: 'logistics' },
    { id: 'in_transit', lane: 'logistics', group: 'logistics' },
    { id: 'delivered', lane: 'logistics', group: 'logistics' },
];

export const KANBAN_COMPLETE_ZONE = 'complete';

export function kanbanColumnLabel(columnId) {
    const production = productionLabel(columnId);
    if (production && production !== columnId) {
        return production;
    }
    return logisticsLabel(columnId);
}

export function isOrderCompleted(order) {
    return Boolean(order?.completed || order?.completed_at);
}

export function canCompleteOrder(order) {
    if (!order || isOrderCompleted(order) || !order.payment_details?.paid) {
        return false;
    }
    return order.logistics_status === 'delivered'
        || order.logistics_status === 'not_delivered'
        || order.logistics_status === 'returned';
}

export function orderKanbanColumn(order) {
    if (isOrderCompleted(order)) {
        return '';
    }
    const production = order?.production_status || '';
    const logistics = order?.logistics_status || '';

    if (logistics === 'not_delivered' || logistics === 'returned') {
        return '';
    }
    if (production === 'queued' || production === 'in_progress') {
        return production;
    }
    if (production === 'done') {
        if (!logistics || logistics === 'blocked' || logistics === 'not_applicable') {
            return 'done';
        }
        return logistics;
    }
    if (logistics && logistics !== 'not_applicable' && logistics !== 'blocked') {
        return logistics;
    }
    return '';
}

export function kanbanMoveSteps(order, columnId) {
    if (columnId === KANBAN_COMPLETE_ZONE) {
        return [{ lane: 'completion', status: 'completed' }];
    }
    const column = KANBAN_COLUMNS.find((item) => item.id === columnId);
    if (!column || orderKanbanColumn(order) === columnId) {
        return [];
    }
    const steps = [];
    if (column.lane === 'production') {
        steps.push({ lane: 'production', status: columnId });
        return steps;
    }
    if (order.production_status === 'queued' || order.production_status === 'in_progress') {
        steps.push({ lane: 'production', status: 'done' });
    }
    steps.push({ lane: 'logistics', status: columnId });
    return steps;
}

export function applyOrderStageSteps(order, steps) {
    const next = { ...order };
    (steps || []).forEach((step) => {
        if (step.lane === 'production') {
            next.production_status = step.status;
        }
        if (step.lane === 'logistics') {
            next.logistics_status = step.status;
        }
        if (step.lane === 'payout') {
            next.payout_status = step.status;
        }
        if (step.lane === 'completion') {
            next.completed = true;
            next.completed_at = next.completed_at || new Date().toISOString();
            if (next.payout_status === 'held') {
                next.payout_status = 'released';
            }
        }
    });
    return next;
}

const LOGISTICS_LABELS = {
    not_applicable: 'Não se aplica',
    blocked: 'Aguardando produção',
    awaiting_pack: 'Aguardando embalagem',
    packing: 'Embalando',
    packed: 'Embalado',
    dispatched: 'Despachado',
    in_transit: 'Saiu para entrega',
    delivered: 'Entregue',
    not_delivered: 'Não entregue',
    returned: 'Devolvido',
};

const PAYOUT_LABELS = {
    not_applicable: 'Não se aplica',
    held: 'A liberar',
    released: 'Liberado',
};

export function normalizeChannelKind(raw) {
    const value = String(raw || '').trim();
    if (CHANNEL_LABELS[value]) {
        return value;
    }
    return CHANNEL_IN_PERSON;
}

export function normalizeProductKind(raw) {
    const value = String(raw || '').trim();
    if (PRODUCT_LABELS[value]) {
        return value;
    }
    return PRODUCT_STOCK;
}

export function channelKindLabel(kind) {
    return CHANNEL_LABELS[normalizeChannelKind(kind)] || CHANNEL_LABELS[CHANNEL_IN_PERSON];
}

export function productKindLabel(kind) {
    return PRODUCT_LABELS[normalizeProductKind(kind)] || PRODUCT_LABELS[PRODUCT_STOCK];
}

export function productionLabel(status) {
    return PRODUCTION_LABELS[status] || status || '—';
}

export function logisticsLabel(status) {
    return LOGISTICS_LABELS[status] || status || '—';
}

export function payoutLabel(status) {
    return PAYOUT_LABELS[status] || status || '—';
}

export function channelHoldsPayoutDefault(kind) {
    const normalized = normalizeChannelKind(kind);
    return normalized === CHANNEL_MARKETPLACE || normalized === CHANNEL_DIGITAL;
}

export function productAllowedForChannel(channelKind, productKind) {
    const channel = normalizeChannelKind(channelKind);
    const product = normalizeProductKind(productKind);
    if (channel === CHANNEL_DIGITAL) {
        return product === PRODUCT_DIGITAL;
    }
    if (channel === CHANNEL_MARKETPLACE) {
        return product === PRODUCT_STOCK || product === PRODUCT_MTO;
    }
    return true;
}

export function productFilterHint(channelKind) {
    switch (normalizeChannelKind(channelKind)) {
        case CHANNEL_DIGITAL:
            return 'Este canal aceita somente produtos digitais.';
        case CHANNEL_MARKETPLACE:
            return 'Este canal aceita somente produtos físicos (estoque ou sob encomenda).';
        default:
            return '';
    }
}

export function channelNeedsShipping(channelKind) {
    return normalizeChannelKind(channelKind) !== CHANNEL_DIGITAL;
}

export function channelForcesPaid(channelKind) {
    const kind = normalizeChannelKind(channelKind);
    return kind === CHANNEL_MARKETPLACE || kind === CHANNEL_DIGITAL;
}

export function nextProductionStatus(current) {
    if (current === 'queued') {
        return 'in_progress';
    }
    if (current === 'in_progress') {
        return 'done';
    }
    return '';
}

export function nextLogisticsStatus(current) {
    const flow = ['awaiting_pack', 'packing', 'packed', 'dispatched', 'in_transit', 'delivered'];
    const index = flow.indexOf(current);
    if (index >= 0 && index < flow.length - 1) {
        return flow[index + 1];
    }
    return '';
}

export function canMarkLogisticsFailed(current) {
    return current === 'dispatched' || current === 'in_transit';
}

export function productionChipColor(status) {
    if (status === 'done' || status === 'not_applicable') {
        return 'success';
    }
    if (status === 'in_progress') {
        return 'warning';
    }
    return 'default';
}

export function logisticsChipColor(status) {
    if (status === 'delivered' || status === 'not_applicable') {
        return 'success';
    }
    if (status === 'not_delivered' || status === 'returned') {
        return 'error';
    }
    if (status === 'blocked') {
        return 'default';
    }
    return 'info';
}

export function payoutChipColor(status) {
    if (status === 'released' || status === 'not_applicable') {
        return 'success';
    }
    if (status === 'held') {
        return 'warning';
    }
    return 'default';
}
